import { type JSX } from 'react';

import type {
  Bedarfsdeckung,
  Belegung,
  Belegungsressource,
  Setzbar,
  Terminbedarf,
  Terminbelegung,
} from '../api/tagesplanung';

/**
 * Der Tagesplan als Raster — die lesbare Ansicht (TAG-03, TAG-08).
 *
 * ============================================================================
 * DER SATZ, AUS DEM DIESE DATEI ENTSTAND
 * ============================================================================
 *
 * Auftraggeber, 13.09.2026: „in der trainingsplanung sind die bausteine im
 * tagesplan sehr gestaucht, baue eine ansicht, in der man den inhalt der
 * felder besser lesen und sie verschieben kann."
 *
 * Der Befund trifft zu, und er folgt aus der Geometrie: Auf der Zeitachse
 * bestimmt die DAUER die Breite. Eine Station von 45 Minuten auf einem
 * Betriebstag von zehn Stunden bekommt 7,5 % der Bahn — bei 700 Pixeln
 * Bahnbreite sind das 52 Pixel für „Slalom nass 09:15–10:00 · Trainer Meier,
 * Schulungsraum 1". Der Rest steht im `title` und damit nirgends, wo man ihn
 * liest. Kein Schriftgrad und kein Umbruch helfen dagegen: Der Platz ist eine
 * Funktion der Dauer, nicht der Gestaltung.
 *
 * Das Raster dreht die Achse. Die Zeit läuft nach UNTEN, jeder Termin bekommt
 * eine Spalte, und damit bekommt jede Station die ganze Spaltenbreite für
 * ihren Text. Was auf der Bahn in den `title` gedrängt war, steht hier im
 * Feld: Baustein, Zeit, Dauer, Phase, zugeteilte Ressourcen und — das ist die
 * eigentliche Arbeit — was noch fehlt.
 *
 * ============================================================================
 * WARUM DAS KEIN ÄNDERUNGSANTRAG IST
 * ============================================================================
 *
 * ÄA-09 hat die Zeitachse eingeführt und dabei selbst gesagt, was sie ist:
 *
 * > „Sie ersetzt das Raster nicht in der Sache, sondern in der Darstellung;
 * > TAG-03 verlangt ein Raster aus Blöcken und Ressourcen und nicht eine
 * > bestimmte Tabellenform."
 *
 * Eine zweite Darstellung derselben Sache fasst keine Anforderung an. Die
 * Zeitachse BLEIBT und bleibt die Vorbelegung — sie zeigt den Tag am Stück,
 * und das kann das Raster nicht. Beide Ansichten arbeiten auf demselben
 * Zustand: Was in der einen angefasst ist, ist es in der anderen auch.
 *
 * ============================================================================
 * WARUM DIE GEMEINSAMEN TEILE HIER STEHEN
 * ============================================================================
 *
 * `Terminkopf` und `SetzWahl` gehören beiden Ansichten. Zweimal geschrieben
 * liefen sie auseinander — und dann sagte die Spalte etwas anderes als die
 * Zeile, obwohl beide denselben Termin meinen. Eine Stelle, zwei Verwender.
 */

/**
 * Ein Abschnitt der Zeit eines Termins: belegt oder frei.
 *
 * BEIDES WIRD GEZEICHNET. Ein Plan, der nur das Belegte zeigt, beantwortet die
 * einzige Frage nicht, die man an einen Tagesplan stellt — wo ist noch Platz
 * (TAG-03).
 *
 * `von` ist der erste Block, `laenge` die Zahl der Blöcke. Die Zeitachse
 * rechnet daraus Prozente, das Raster Zeilen — dieselbe Zahl, zwei Bilder.
 */
export type Abschnitt =
  | { readonly art: 'belegt'; readonly von: number; readonly laenge: number; readonly b: Belegung }
  | { readonly art: 'frei'; readonly von: number; readonly laenge: number };

/**
 * Ein Termin mit allem, was eine Ansicht von ihm zeigt.
 *
 * Das Modell wird EINMAL gebaut und von beiden Ansichten gelesen. Vorher
 * holte die Zeitachse sich jedes Stück mit einem eigenen Griff aus dem Plan
 * (`befunde(t.termin)`, `begleitetVon(t.termin)` …) — bei zwei Ansichten wären
 * das zwei Sätze solcher Griffe, und der zweite hinkt hinterher.
 */
export type Terminspalte = {
  readonly termin: string;
  /** Der Artikel — die Bausteinwahl bietet nur das Programm DIESES Produkts (PRD-29). */
  readonly artikel: string;
  readonly artikelnummer: string;
  readonly produkt: string;
  /** Die Anfangszeit des Termins (AeA-08: „verschiedene Anfangszeiten"). */
  readonly ab: string;
  /** BUC-66: Beim Exklusivtermin der Kunde, sonst die Vertriebsform. */
  readonly wem: string;
  readonly befunde: { readonly zwingend: number; readonly hinweis: number };
  /** TAG-15, TAG-71: Wer den Termin den ganzen Tag begleitet. */
  readonly begleiter: readonly Terminbelegung[];
  /** Nur das, was noch FEHLT — gedeckter Bedarf ist keine Nachricht. */
  readonly fehlbedarf: readonly Terminbedarf[];
  /**
   * TAG-13, TAG-20: Der Termin ist zur PROBE abgewählt.
   *
   * Er steht weiter da — ausgeblendet wäre schlechter: Wer ihn abwählt, will
   * sehen, was ohne ihn geschieht, und nicht, dass er verschwindet. Die
   * Rechnung lässt ihn weg; die Spalte sagt es.
   */
  readonly probeAus?: boolean;
  readonly abschnitte: readonly Abschnitt[];
};

/** Eine Zeile des Rasters: ein Zeitabschnitt, an dem irgendwo etwas wechselt. */
export type Rasterzeile = { readonly von: number; readonly laenge: number };

/**
 * Die Zeilen des Rasters — nicht je Block eine, sondern je WECHSEL eine.
 *
 * ============================================================================
 * WARUM NICHT VIERZIG ZEILEN
 * ============================================================================
 *
 * Ein Betriebstag von zehn Stunden hat vierzig Viertelstundenblöcke (AeA-07).
 * Je Block eine Zeile ergäbe eine Tabelle, durch die man scrollt, um zu sehen,
 * was um halb elf läuft — genau der Vorwurf, den ÄA-09 gegen das alte Raster
 * erhoben hat, und er war berechtigt.
 *
 * Gesucht sind deshalb die GRENZEN: jeder Block, an dem bei irgendeinem Termin
 * ein Abschnitt beginnt oder endet. Zwischen zwei Grenzen ändert sich in keiner
 * Spalte etwas, also braucht es dort keine Zeile. Ein Tag mit zwei Terminen und
 * je acht Stationen kommt so auf ein Dutzend Zeilen statt vierzig.
 *
 * Weil JEDER Abschnitt an einer Grenze beginnt und an einer endet, überdeckt
 * jeder eine ganze Zahl von Zeilen — das ist die Voraussetzung dafür, dass
 * `rowSpan` aufgeht.
 *
 * DIE ZEILENHÖHE TRÄGT DAMIT KEINE DAUER MEHR. Das ist Absicht und der ganze
 * Sinn: Eine Viertelstunde soll so gut lesbar sein wie zwei Stunden. Wie lange
 * etwas dauert, steht als Zeit und als Minutenzahl im Feld — vorlesbar,
 * druckbar, und nicht als Länge, die man schätzen muss (NFA-14, WCAG 1.4.1).
 */
export function rasterzeilen(
  spalten: readonly Terminspalte[],
  ersterBlock: number,
  blockzahl: number,
): readonly Rasterzeile[] {
  /*
   * OHNE TERMIN KEINE ZEILE. Der Tag hat dann Blöcke, aber nichts, was in ihnen
   * stünde — eine Zeile mit einer Uhrzeit und keinem Feld behauptete einen
   * Abschnitt, den niemand füllt.
   */
  if (spalten.length === 0) return [];

  const ende = ersterBlock + blockzahl;
  const grenzen = new Set<number>([ersterBlock, ende]);
  for (const s of spalten) {
    for (const a of s.abschnitte) {
      grenzen.add(a.von);
      grenzen.add(a.von + a.laenge);
    }
  }
  const sortiert = [...grenzen].filter((g) => g >= ersterBlock && g <= ende).sort((a, b) => a - b);
  const zeilen: Rasterzeile[] = [];
  for (let i = 0; i + 1 < sortiert.length; i += 1) {
    zeilen.push({ von: sortiert[i]!, laenge: sortiert[i + 1]! - sortiert[i]! });
  }
  return zeilen;
}

/**
 * Der Kopf eines Termins — in der Zeitachse die Zeile, im Raster die Spalte.
 *
 * Er sagt, WESSEN Gruppe es ist (BUC-66), was an ihr hängt (TAG-13) und wer
 * sie begleitet (TAG-15, TAG-71). Dieselben Angaben in beiden Ansichten, aus
 * einer Quelle.
 */
export function Terminkopf({
  spalte,
  beiTermin,
}: {
  readonly spalte: Terminspalte;
  /**
   * Der Weg ins Terminfenster (Auftraggeber, 13.09.2026).
   *
   * Freiwillig: Der Kopf steht auch dort, wo es kein Fenster gibt — dann ist
   * der Name Text. Ein Knopf, der nichts tut, wäre die schlechtere Auskunft.
   */
  readonly beiTermin?: (termin: string) => void;
}): JSX.Element {
  return (
    <>
      {spalte.probeAus === true && (
        <span className="marke marke--inaktiv">nicht in der Rechnung</span>
      )}
      {beiTermin === undefined ? (
        spalte.produkt
      ) : (
        <button
          type="button"
          className="knopf knopf--verweis"
          onClick={() => {
            beiTermin(spalte.termin);
          }}
        >
          {spalte.produkt}
        </button>
      )}
      <br />
      <span className="hilfe">
        {spalte.artikelnummer} · ab {spalte.ab} · {spalte.wem}
      </span>
      {/*
        WELCHE ZEILE GEMEINT IST (TAG-13, 0175).

        Der Befund nennt Produkt und Uhrzeit — bei zwei Gruppen desselben
        Trainings zur selben Zeit (E-38) trifft das auf beide zu. Die Marke
        sagt, an welcher von beiden etwas hängt; die Liste oben sagt, was.
      */}
      {/*
        KONFLIKTE IN DER ACHTUNGSFARBE (ÄA-41). Der Entwurf kennt keine
        Fehlerfarbe; zwingender Konflikt und Hinweis stehen beide in Rost, die
        Schwere nach TAG-24 trägt das Wort.
      */}
      {spalte.befunde.zwingend > 0 && (
        <span className="marke marke--warn">
          {spalte.befunde.zwingend} {spalte.befunde.zwingend === 1 ? 'Konflikt' : 'Konflikte'}
        </span>
      )}
      {spalte.befunde.hinweis > 0 && (
        <span className="marke marke--warn">
          {spalte.befunde.hinweis} {spalte.befunde.hinweis === 1 ? 'Hinweis' : 'Hinweise'}
        </span>
      )}
      <br />
      {/*
        WER DEN TERMIN BEGLEITET (TAG-15, TAG-71).

        Der Auftraggeber am 08.09.2026: „Ein Trainer wird nicht einer Ressource
        zugeordnet, sondern ein Trainer wird einem Termin zugeordnet." Er
        begleitet die Gruppe durch den ganzen Tag — deshalb steht er am Termin
        und nicht an einer einzelnen Station. Der Teilnehmer sieht beim Check-in
        „den Namen des betreuenden Trainers" (TAG-71), einen.

        ZUGETEILT WIRD IN DER RESSOURCENVERWALTUNG. Eine Belegung ist ein
        eigener Vorgang mit eigener Verfügbarkeitsprüfung (RES-14 bis RES-18);
        sie hier nachzubauen hieße, zwei Wege zur selben Sache zu führen. Der
        Tagesplan zeigt, was steht und was fehlt.
      */}
      {spalte.begleiter.length === 0 ? (
        <span className="hilfe">niemand zugeteilt</span>
      ) : (
        spalte.begleiter.map((r) => (
          <span key={r.belegung} className={r.angefragt ? 'marke marke--warn' : 'marke'}>
            {r.bezeichnung}
            {/*
              RES-22: „Sie blockiert die Ressource, IST ABER ALS VORLÄUFIG
              ERKENNBAR."

              Der erste Entwurf las dafür `hart` — und der ist bei einer
              vorläufigen Zuordnung WAHR: Er folgt aus dem Status, und
              vorläufig, bestätigt und zugesagt zählen alle als hart. Die Marke
              erschien deshalb nie. Sichtbar wird der Unterschied nur am Status
              selbst.

              UND „ANGEFRAGT" IST NICHT DASSELBE WIE „VORLÄUFIG" (RES-33).
              Beide tragen denselben Status: Die Zuteilung aus der Tagesplanung
              (TAG-82) disponiert, die Anfrage wartet auf eine Antwort. Wer das
              gleich liest, hält eine laufende Frist für einen besetzten Termin.
            */}
            {r.angefragt ? (
              <span className="hilfe">
                {' '}
                · angefragt
                {r.frist !== null
                  && `, Frist ${new Date(r.frist).toLocaleDateString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    timeZone: 'Europe/Berlin',
                  })}`}
              </span>
            ) : (
              r.status !== 'bestaetigt' && <span className="hilfe"> · {r.status}</span>
            )}
          </span>
        ))
      )}
      {/* TAG-15, RES-24: Was fehlt, ist ein Konflikt, wenn es zwingend ist (ÄA-41: Rost). */}
      {spalte.fehlbedarf.map((b) => (
        <span key={b.bedarf}>
          {' '}
          <span className={b.zwingend ? 'marke marke--warn' : 'marke'}>
            {b.bedarfsname} fehlt ({b.gedeckt} von {b.verlangt})
          </span>
        </span>
      ))}
    </>
  );
}

/**
 * Die Wahl eines Bausteins für eine freie Stelle (TAG-08, PRD-29).
 *
 * Angeboten werden die Bausteine DIESES Produkts, nach Phase geordnet — wer
 * eine Station nachträgt, sucht sie im Programm und nicht unter allen fünfzig
 * des Katalogs. Ein Ersatz von außerhalb bleibt möglich, braucht aber den
 * ganzen Katalog; das ist ein eigener Weg und kein Nebeneffekt dieser Liste.
 */
export function SetzWahl({
  bausteine,
  beiWahl,
  beiAbbruch,
}: {
  readonly bausteine: readonly Setzbar[];
  readonly beiWahl: (baustein: string) => void;
  readonly beiAbbruch: () => void;
}): JSX.Element {
  if (bausteine.length === 0) {
    return (
      <span className="hilfe">
        Dieses Produkt führt keine Bausteine.{' '}
        <button type="button" className="knopf knopf--verweis" onClick={beiAbbruch}>
          zurück
        </button>
      </span>
    );
  }
  return (
    <>
      <select
        aria-label="Baustein einplanen"
        defaultValue=""
        onChange={(e) => {
          if (e.target.value !== '') beiWahl(e.target.value);
        }}
      >
        <option value="">Baustein wählen …</option>
        {bausteine.map((x) => (
          <option key={x.baustein} value={x.baustein}>
            {x.bezeichnung} ({x.dauerBloecke} Blöcke)
          </option>
        ))}
      </select>{' '}
      <button type="button" className="knopf knopf--verweis" onClick={beiAbbruch}>
        Abbrechen
      </button>
    </>
  );
}

export type Tagesrasterprops = {
  readonly spalten: readonly Terminspalte[];
  /** Der Weg ins Terminfenster — der Kopf jeder Spalte führt hinein. */
  readonly beiTermin?: (termin: string) => void;
  readonly zeilen: readonly Rasterzeile[];
  /**
   * Der Beginn eines Blocks als `HH:MM` — `undefined`, wenn der Tag dort endet.
   *
   * Das Ende eines Blocks ist der Beginn des nächsten; nur der letzte hat
   * keinen Nachfolger, und dort gilt `tagesende`. So steht die Uhrzeit auch am
   * freien Feld, das keine Belegung mit eigenen Zeiten hat.
   */
  readonly beginnVon: (block: number) => string | undefined;
  readonly tagesende: string;
  /** AeA-07: die Blocklänge kommt aus dem Tag und steht nicht im Quelltext. */
  readonly blocklaengeMinuten: number;
  readonly darfPlanen: boolean;
  readonly angefasst: string | null;
  /** Wo gerade ein Baustein von Hand gesetzt wird: `Block:Termin`. */
  readonly setzstelle: string | null;
  readonly setzbar: readonly Setzbar[];
  readonly ressourcenDer: (belegung: string) => readonly Belegungsressource[];
  readonly bedarfDer: (belegung: string) => readonly Bedarfsdeckung[];
  /** Klick auf eine Station: anfassen, loslassen oder tauschen (TAG-08). */
  readonly beiBelegung: (b: Belegung) => void;
  /** Klick auf eine freie Stelle: dorthin verschieben oder Baustein setzen. */
  readonly beiFreiemBlock: (block: number, termin: string) => void;
  /**
   * Der Beginn eines Zugs — anfassen, ohne die Klickbedeutung mitzunehmen.
   *
   * `beiBelegung` schaltet um: dieselbe Station noch einmal lässt LOS, eine
   * andere desselben Termins TAUSCHT. Beim Ziehen wäre beides falsch; ein Zug
   * fängt immer mit demselben Griff an.
   */
  readonly beiZiehen: (b: Belegung) => void;
  /** Abwurf auf eine freie Stelle — verschieben. */
  readonly beiAbwurfFrei: (block: number, termin: string) => void;
  /** Abwurf auf eine belegte Stelle — tauschen. */
  readonly beiAbwurfAuf: (b: Belegung) => void;
  readonly beiSetzen: (termin: string, block: number, baustein: string) => void;
  readonly beiAbbruch: () => void;
};

/**
 * Der Tag als Raster: die Zeit nach unten, je Termin eine Spalte.
 *
 * ============================================================================
 * VERSCHIEBEN GEHT AUF ZWEI WEGEN, UND DER ERSTE IST DER KLICK
 * ============================================================================
 *
 * Anfassen und Ziel wählen sind zwei Klicks — das geht mit der Maus, mit dem
 * Finger und mit der Tastatur, und ein Vorleseprogramm kann es ansagen. Diese
 * Entscheidung stand schon an der Zeitachse und bleibt: Sie ist der Weg, der
 * für ALLE funktioniert.
 *
 * Im Raster kommt das Ziehen dazu. Nicht, weil Klicken nicht reichte, sondern
 * weil die Felder jetzt groß genug sind, dass ein Zug trifft: Auf der Bahn ist
 * ein Abschnitt 52 Pixel breit, hier ist er eine ganze Spalte. Beide Wege
 * enden im selben Handgriff — es gibt keinen zweiten Regelsatz dafür, was ein
 * Verschieben darf.
 *
 * WAS GEZOGEN WERDEN KANN, IST SICHTBAR: Solange etwas in der Hand liegt,
 * tragen die möglichen Ziele — die freien Stellen und die anderen Stationen
 * DESSELBEN Termins — eine Markierung. Über Termine hinweg wird nicht
 * getauscht: Was eine Gruppe fährt, geht die andere nichts an (TAG-07).
 */
export function Tagesraster({
  spalten,
  beiTermin,
  zeilen,
  beginnVon,
  tagesende,
  blocklaengeMinuten,
  darfPlanen,
  angefasst,
  setzstelle,
  setzbar,
  ressourcenDer,
  bedarfDer,
  beiBelegung,
  beiFreiemBlock,
  beiZiehen,
  beiAbwurfFrei,
  beiAbwurfAuf,
  beiSetzen,
  beiAbbruch,
}: Tagesrasterprops): JSX.Element {
  /** Die Uhrzeit, zu der ein Block zu Ende ist — der nächste beginnt dann. */
  const endeVon = (block: number): string => beginnVon(block) ?? tagesende;

  const dauer = (bloecke: number): string => `${String(bloecke * blocklaengeMinuten)} Min`;

  /*
   * ZU WELCHEM TERMIN DAS ANGEFASSTE GEHÖRT.
   *
   * Aus den Spalten gelesen und nicht als Merkmal übergeben: Die Antwort steht
   * in den Daten, die diese Ansicht ohnehin hat. Ein zweites Merkmal dafür
   * wäre eine zweite Wahrheit.
   */
  const gehalten = spalten.find((s) =>
    s.abschnitte.some((a) => a.art === 'belegt' && a.b.belegung === angefasst),
  );

  /** Wie viele Zeilen ein Abschnitt überdeckt — die Grundlage für `rowSpan`. */
  const spanne = (a: Abschnitt): number =>
    zeilen.filter((z) => z.von >= a.von && z.von < a.von + a.laenge).length;

  return (
    <div className="rasterplan__rolle">
      <table className="liste__tabelle rasterplan" aria-label="Tagesplan als Raster">
        <thead>
          <tr>
            <th scope="col" className="rasterplan__zeitkopf">
              Zeit
            </th>
            {spalten.map((s) => (
              /*
               * EIGENE KLASSE, WEIL DER SPALTENKOPF HIER KEIN LABEL IST.
               *
               * `.liste__tabelle thead th` setzt die Auszeichnung des Hauses:
               * Versalien mit 13 % Sperrung, 10 Pixel. Das ist richtig für
               * „TERMINE" und „STAND" — und falsch für „ADAC Pkw-Intensiv-
               * Training · AT-000007 · Einzelplatzverkauf". Hier steht ein
               * Name, kein Spaltentitel.
               */
              <th scope="col" key={s.termin} className="rasterplan__terminkopf">
                <Terminkopf spalte={s} {...(beiTermin === undefined ? {} : { beiTermin })} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {zeilen.map((z) => (
            <tr key={z.von}>
              {/*
                DIE UHRZEIT STEHT AM ZEILENKOPF UND NICHT NUR IM FELD.

                Sie ist die Ordnung der Tabelle: Wer wissen will, was um halb
                elf läuft, sucht die Zeile und liest quer. Steht die Zeit nur in
                den Feldern, muss er sie in jeder Spalte einzeln suchen.
              */}
              <th scope="row" className="rasterplan__zeit">
                {beginnVon(z.von) ?? tagesende}
                <br />
                <span className="hilfe">bis {endeVon(z.von + z.laenge)}</span>
              </th>
              {spalten.map((s) => {
                /*
                 * NUR DER ABSCHNITT, DER HIER BEGINNT, bekommt ein Feld. Was
                 * darüber anfing, reicht mit `rowSpan` herein — ein zweites
                 * Feld dafür schöbe die Spalte um eine Stelle nach rechts.
                 */
                const a = s.abschnitte.find((x) => x.von === z.von);
                if (a === undefined) return null;

                const zielHier = gehalten !== undefined && gehalten.termin === s.termin;

                if (a.art === 'frei') {
                  /* Die Stelle, an der gerade ein Baustein gewählt wird. */
                  if (setzstelle === `${String(a.von)}:${s.termin}`) {
                    return (
                      <td key={s.termin} rowSpan={spanne(a)} className="rasterplan__feld">
                        <SetzWahl
                          bausteine={setzbar.filter((x) => x.artikel === s.artikel)}
                          beiWahl={(baustein) => {
                            beiSetzen(s.termin, a.von, baustein);
                          }}
                          beiAbbruch={beiAbbruch}
                        />
                      </td>
                    );
                  }
                  return (
                    <td key={s.termin} rowSpan={spanne(a)} className="rasterplan__feld">
                      <button
                        type="button"
                        className={
                          zielHier
                            ? 'plan__karte plan__karte--frei plan__karte--ziel'
                            : 'plan__karte plan__karte--frei'
                        }
                        disabled={!darfPlanen}
                        onClick={() => {
                          beiFreiemBlock(a.von, s.termin);
                        }}
                        onDragOver={(e) => {
                          /*
                           * OHNE `preventDefault` NIMMT DER BROWSER NICHTS AN.
                           * Das ist keine Formalie: Ohne diese Zeile zeigt der
                           * Zeiger das Verbotsschild, und niemand sucht die
                           * Ursache in einer fehlenden Zeile Code.
                           */
                          if (darfPlanen) e.preventDefault();
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          beiAbwurfFrei(a.von, s.termin);
                        }}
                      >
                        <span className="plan__karte-kopf">
                          {beginnVon(a.von) ?? tagesende}–{endeVon(a.von + a.laenge)} frei
                        </span>
                        <span className="plan__karte-zeile">{dauer(a.laenge)}</span>
                        {zielHier && (
                          <span className="plan__karte-zeile">
                            <strong>hierhin verschieben</strong>
                          </span>
                        )}
                      </button>
                    </td>
                  );
                }

                const gewaehlt = a.b.belegung === angefasst;
                const ressourcen = ressourcenDer(a.b.belegung);
                /* Nur die LÜCKEN: Gedeckter Bedarf steht schon als Ressource da. */
                const luecken = bedarfDer(a.b.belegung).filter((d) => d.zugeteilt < d.verlangt);

                return (
                  <td key={s.termin} rowSpan={spanne(a)} className="rasterplan__feld">
                    <button
                      type="button"
                      className={[
                        'plan__karte',
                        gewaehlt ? 'plan__karte--gewaehlt' : '',
                        !gewaehlt && zielHier ? 'plan__karte--ziel' : '',
                      ]
                        .filter((k) => k !== '')
                        .join(' ')}
                      disabled={!darfPlanen}
                      aria-pressed={darfPlanen ? gewaehlt : undefined}
                      draggable={darfPlanen}
                      onClick={() => {
                        beiBelegung(a.b);
                      }}
                      onDragStart={() => {
                        beiZiehen(a.b);
                      }}
                      onDragOver={(e) => {
                        if (darfPlanen) e.preventDefault();
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        beiAbwurfAuf(a.b);
                      }}
                    >
                      {/*
                        DER NAME STEHT OBEN UND GANZ.

                        Das war der Anlass: Auf der Bahn brach der Name nach
                        drei Zeichen ab. Hier bricht er um — `white-space:
                        normal` in der Stildatei, und die Zeile wird so hoch,
                        wie der Name braucht.
                      */}
                      <span className="plan__karte-kopf">
                        {a.b.bausteinname ?? a.b.aktivitaetsart}
                      </span>
                      <span className="plan__karte-zeile">
                        {a.b.vonZeit}–{a.b.bisZeit} · {dauer(a.b.laenge)}
                        {a.b.phase !== null && ` · Phase ${String(a.b.phase)}`}
                      </span>
                      {/*
                        TAG-08: „die Herkunft aus der Vorlage und jede
                        Abweichung davon bleiben erkennbar."
                      */}
                      {a.b.abweichend && (
                        <span className="plan__karte-zeile">
                          <span className="marke marke--warn">abweichend</span>
                        </span>
                      )}
                      {ressourcen.length > 0 && (
                        <span className="plan__karte-zeile">
                          {ressourcen.map((r) => (
                            <span key={r.ressource} className="marke">
                              {r.bezeichnung}
                            </span>
                          ))}
                        </span>
                      )}
                      {/*
                        WAS FEHLT, STEHT AM FELD (PRD-29, TAG-24).

                        „Die Ressourcen müssen auf Basis der Bausteine geplant
                        werden" — dann muss am Baustein stehen, was ihm fehlt.
                        Auf der Zeitachse war dafür kein Platz; es stand nur in
                        der Arbeitsliste der einen angefassten Station, also
                        nacheinander für vierzig Stationen. Hier steht es für
                        alle gleichzeitig, und der Planer sieht mit einem Blick,
                        wo Arbeit liegt.
                      */}
                      {luecken.map((d) => (
                        <span key={d.bedarf} className="plan__karte-zeile">
                          {/* TAG-24, ÄA-41: Die zwingende Lücke ist ein Konflikt und steht in Rost. */}
                          <span className={d.zwingend ? 'marke marke--warn' : 'marke'}>
                            {d.bedarfsname} fehlt ({d.zugeteilt} von {d.verlangt})
                          </span>
                        </span>
                      ))}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
